/**
 * /portal — kanban dashboard (T-037).
 *
 * The default portal view: three-column kanban (Backlog / Active / Delivered)
 * over the subscriber's own tasks (PR-08, US-07). The active-slot limit is
 * surfaced visibly per tier (1 Studio / 2 Pro, PR-13).
 *
 * This is the index child of the /portal layout route, so it renders inside
 * PortalLayout and inherits the portal entry guard. Tier comes from the
 * parent route context (no re-fetch of the subscriber row).
 *
 * The transient welcome view (T-038) is rendered here, ahead of the kanban,
 * when onboarding_state carries a welcome block that has not been dismissed.
 */

import { useEffect, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import type { Task } from '@studio/api-schemas';
import { activeSlotsForTier, fetchOwnTasks } from '@/lib/portal';
import { AsyncPanel, type AsyncState } from '@/components/portal/AsyncPanel';
import { Kanban } from '@/components/portal/Kanban';
import { WelcomeView } from '@/components/portal/WelcomeView';
import {
  PortalInputRequestCard,
  fetchGeneralInputRequest,
  type RecipientInputRequest,
} from '@/components/input-requests';

export const Route = createFileRoute('/portal/')({
  component: PortalDashboard,
});

function PortalDashboard() {
  const { portal } = Route.useRouteContext();
  const [state, setState] = useState<AsyncState<Task[]>>({
    status: 'loading',
  });
  // Surface D (PRD-005 / ARCH-028, IR-T-10): the general input-request card on
  // the portal home. Loaded independently of the task list so a failed read
  // never blocks the kanban; absent when there is no request.
  const [generalRequest, setGeneralRequest] =
    useState<RecipientInputRequest | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchOwnTasks()
      .then((tasks) => {
        if (!cancelled) setState({ status: 'ready', data: tasks });
      })
      .catch(() => {
        if (!cancelled)
          setState({
            status: 'error',
            message:
              'Could not load your tasks. Refresh in a moment, or use the contact form if it keeps failing.',
          });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const row = await fetchGeneralInputRequest();
        if (!cancelled) setGeneralRequest(row);
      } catch {
        if (!cancelled) setGeneralRequest(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const activeSlots = activeSlotsForTier(
    portal.tier === 'pro' ? 'pro' : 'studio',
  );

  return (
    <div className="space-y-8">
      <WelcomeView />
      {generalRequest ? (
        <div className="max-w-[640px]">
          <PortalInputRequestCard request={generalRequest} light />
        </div>
      ) : null}
      <div>
        <div className="mb-7 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="mb-2.5 text-[11px] font-medium uppercase tracking-[0.08em] text-[color:var(--color-text-mute)]">
              Dashboard
            </p>
            <h1 className="font-display italic text-[clamp(28px,4vw,34px)] leading-[1.1] tracking-[-0.01em] text-[color:var(--color-black)]">
              Your backlog.
            </h1>
          </div>
          <Link
            to="/portal/tasks/new"
            className="inline-flex items-center justify-center gap-1.5 rounded-md bg-[color:var(--color-navy)] px-[22px] py-2.5 text-sm font-medium text-[color:var(--color-linen)] transition-colors hover:bg-[color:var(--color-navy-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-rose)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--color-white)]"
          >
            + New task
          </Link>
        </div>
        <p className="mb-6 text-sm text-[color:var(--color-text-mute)]">
          Everything in motion, in one place. Drag a backlog task to change
          what gets pulled next.
        </p>
        <AsyncPanel state={state} loadingLabel="Loading your tasks...">
          {(tasks) => <Kanban tasks={tasks} activeSlots={activeSlots} />}
        </AsyncPanel>
      </div>
    </div>
  );
}
